<script setup lang="ts">
/**
 * One past conversation, read-only — P2-52.
 *
 * NO COMPOSER, and not because this component chose not to draw one: there is
 * nothing it could send. `widget.messages.send` resolves the conversation from
 * the session and accepts no id, so a message typed here would land in the
 * visitor's CURRENT conversation no matter what is on the screen. The absence of
 * the control is the honest rendering of that, and it is why the route that
 * serves this screen is a read and nothing else.
 *
 * The transcript itself is the SAME `Transcript` the live chat uses. A read-only
 * copy would be a second place to get the three rules in its header right — every
 * body a text node, `bodyFormat` ignored, no client-side note filter — and the
 * second copy is the one that gets them wrong.
 */
import { computed } from 'vue';

import type { WidgetView } from '../app/useWidget';
import { useText } from '../vendor/i18n';
import { OwSpinner } from '../vendor/ui';

import ClosedNotice from './ClosedNotice.vue';
import Transcript from './Transcript.vue';
import UnavailableNotice from './UnavailableNotice.vue';

const props = defineProps<{ readonly view: WidgetView }>();

const text = useText();
const past = computed(() => props.view.past);

/**
 * The footer offers a new conversation only when there is no live one to
 * return to. A visitor whose current chat is open and who is reading an old one
 * does not need a second chat; they need the back button, which is where they
 * came from.
 */
const startNew = computed<(() => void) | null>(() => {
  const current = props.view.conversation;
  if (current !== null && current.statusCategory !== 'closed') {
    return null;
  }
  return (): void => {
    // Back FIRST: `startNewConversation` only flips a local blank page, and
    // flipping it while the visitor is still looking at a read-only transcript
    // would leave them with a chat they cannot see and a composer somewhere
    // behind it.
    props.view.backToChat();
    props.view.startNewConversation();
  };
});
</script>

<template>
  <div v-if="past.loading" class="ow-centred">
    <OwSpinner :label="text('widget.conversation.loading')" />
  </div>

  <!--
    A 404 from the policy ("not yours") and a 404 from an id that never existed
    are the same answer by design, so there is one thing to say. The way back is
    the header's back button, which is on screen either way.
  -->
  <UnavailableNotice
    v-else-if="past.failed || past.conversation === null"
    :detail="text('widget.error.unreachable')"
  />

  <template v-else>
    <Transcript :messages="past.messages" />
    <ClosedNotice :closed="past.conversation.statusCategory === 'closed'" :start-new="startNew" />
  </template>
</template>
